import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Toaster } from 'react-hot-toast';
import { getApiBaseUrl } from '../utils/api';

const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [lastOrderCount, setLastOrderCount] = useState(0);

  // Filters and pagination
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  // Confirmation dialog states
  const [confirmOrderToComplete, setConfirmOrderToComplete] = useState(null);
  const [showBalanceConfirmation, setShowBalanceConfirmation] = useState(false);
  const [confirmOrderToDispatch, setConfirmOrderToDispatch] = useState(null);
  const [showDispatchConfirmation, setShowDispatchConfirmation] = useState(false);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });

      const response = await fetch(`${getApiBaseUrl()}/api/orders?${queryParams}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders);
        setPagination(prev => ({
          ...prev,
          total: data.total,
          totalPages: data.totalPages
        }));
      } else {
        toast.error('Failed to load orders');
      }
    } catch (error) {
      toast.error('Failed to load orders');
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/orders/stats/summary`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);

        // Update local state
        setOrders(orders.map(order =>
          order._id === orderId
            ? {
                ...order,
                status: newStatus,
                balance: result.order.balance,
                totalAdvance: result.order.totalAdvance
              }
            : order
        ));

        // Refresh stats
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to update order status');
      }
    } catch (error) {
      toast.error('Failed to update order status');
      console.error('Error updating order status:', error);
    }
  };

  // Mark balance as received
  const markBalanceReceived = async (orderId) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/orders/${orderId}/balance-received`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);

        // Update local state
        setOrders(orders.map(order =>
          order._id === orderId
            ? { ...order, balance: 0, totalAdvance: result.order.totalAdvance }
            : order
        ));

        // Refresh stats
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to mark balance as received');
      }
    } catch (error) {
      toast.error('Failed to mark balance as received');
      console.error('Error marking balance as received:', error);
    }
  };

  // Handle wholesale order completion confirmation
  const handleCompleteWholesaleOrder = (order) => {
    setConfirmOrderToComplete(order);
    setShowBalanceConfirmation(true);
  };

  // Confirm wholesale order completion
  const confirmCompleteWholesaleOrder = async (confirmed) => {
    if (!confirmed || !confirmOrderToComplete) {
      setShowBalanceConfirmation(false);
      setConfirmOrderToComplete(null);
      return;
    }

    // Mark balance as received first if not already received
    if (confirmOrderToComplete.balance > 0) {
      await markBalanceReceived(confirmOrderToComplete._id);
    }

    // Then complete the order
    await updateOrderStatus(confirmOrderToComplete._id, 'completed');

    setShowBalanceConfirmation(false);
    setConfirmOrderToComplete(null);
  };

  // Handle retail order dispatch confirmation
  const handleDispatchRetailOrder = (order) => {
    setConfirmOrderToDispatch(order);
    setShowDispatchConfirmation(true);
  };

  // Confirm retail order dispatch
  const confirmDispatchRetailOrder = async (confirmed) => {
    if (!confirmed || !confirmOrderToDispatch) {
      setShowDispatchConfirmation(false);
      setConfirmOrderToDispatch(null);
      return;
    }

    // Dispatch the retail order (which automatically marks balance as received)
    await updateOrderStatus(confirmOrderToDispatch._id, 'dispatched');

    setShowDispatchConfirmation(false);
    setConfirmOrderToDispatch(null);
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  // Handle search
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrders();
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Initial load
  useEffect(() => {
    fetchStats();
    fetchOrders();
  }, [fetchOrders]);

  // Refetch when filters or pagination change
  useEffect(() => {
    fetchOrders();
  }, [filters, pagination.page, pagination.limit, fetchOrders]);

  // Real-time notifications for new orders
  useEffect(() => {
    const checkForNewOrders = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/orders/stats/summary`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const currentOrderCount = data.totalOrders;

          if (lastOrderCount > 0 && currentOrderCount > lastOrderCount) {
            const newOrders = currentOrderCount - lastOrderCount;
            toast.success(`🔔 ${newOrders} new order${newOrders > 1 ? 's' : ''} received!`, {
              duration: 5000,
              style: {
                background: '#10B981',
                color: 'white',
                fontSize: '16px'
              }
            });
            // Refresh the orders list
            fetchOrders();
          }

          setLastOrderCount(currentOrderCount);
        }
      } catch (error) {
        console.error('Error checking for new orders:', error);
      }
    };

    // Check immediately, then every 30 seconds
    checkForNewOrders();
    const interval = setInterval(checkForNewOrders, 30000);

    return () => clearInterval(interval);
  }, [lastOrderCount, fetchOrders]);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'dispatched': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get display status based on balance and current status
  const getDisplayStatus = (order) => {
    if (order.status === 'cancelled') return 'cancelled';
    if (order.status === 'completed') return 'completed';
    if (order.status === 'dispatched') return 'dispatched';
    if (order.balance > 0) return 'pending'; // Will display as "Pending Payment"
    return order.status;
  };

  // Get status text for display
  const getStatusText = (order) => {
    const status = getDisplayStatus(order);
    if (status === 'pending' && order.balance > 0) {
      return 'Pending Payment';
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getTypeColor = (type) => {
    return type === 'retail' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 py-8">
      <Toaster position="top-right" />
      <div className="container mx-auto px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              📋 Order Management
            </h1>
            <p className="text-gray-600">Manage and track all orders</p>
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800">Total Orders</h3>
              <p className="text-3xl font-bold text-blue-600">{stats.totalOrders || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800">Today's Orders</h3>
              <p className="text-3xl font-bold text-green-600">{stats.todayOrders || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800">Pending</h3>
              <p className="text-3xl font-bold text-yellow-600">
                {stats.statusBreakdown?.find(s => s._id === 'pending')?.count || 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md">
              <h3 className="text-lg font-semibold text-gray-800">Completed</h3>
              <p className="text-3xl font-bold text-green-600">
                {stats.statusBreakdown?.find(s => s._id === 'completed')?.count || 0}
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="retail">Retail</option>
                  <option value="wholesale">Wholesale</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Order No, Name, Phone..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={handleSearch}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Search
                </button>
              </div>
            </div>
          </div>

          {/* Orders Table */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-600">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="p-8 text-center text-gray-600">No orders found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Items & Pricing Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.orderNo}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(order.createdAt)}
                            </div>
                            <div className="text-sm text-gray-500">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(order.type)}`}>
                                {order.type}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {order.customerName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {order.phone}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900 mb-2">
                              Total: {formatCurrency(order.grandTotal)}
                            </div>
                            <div className="text-sm text-gray-500 mb-2">
                              Advance: {formatCurrency(order.totalAdvance)}
                            </div>
                            <div className={`text-sm mb-3 ${order.balance === 0 ? 'text-green-600 font-medium' : 'text-red-600'}`}>
                              {order.balance === 0 ? 'Fully Paid' : `Balance: ${formatCurrency(order.balance)}`}
                            </div>

                            {/* Item Details */}
                            <div className="space-y-1">
                              {order.items.map((item, index) => (
                                <div key={index} className="text-xs bg-gray-50 p-2 rounded border">
                                  <div className="flex items-center gap-2">
                                    {/* Model Photo */}
                                    {item.photo && (
                                      <img
                                        src={item.photo}
                                        alt={`${item.modelName} thumbnail`}
                                        className="w-8 h-8 object-cover rounded border flex-shrink-0"
                                      />
                                    )}
                                    <div className="flex-1">
                                      <div className="font-medium text-gray-800">
                                        {item.modelName} - {item.modelNumber}
                                      </div>
                                      <div className="text-gray-600 mt-1">
                                        Qty: {item.qty} × {formatCurrency(item.unitPrice)} = {formatCurrency(item.totalValue)}
                                      </div>
                                      {item.advance > 0 && (
                                        <div className="text-blue-600">
                                          Advance: {formatCurrency(item.advance)}
                                        </div>
                                      )}
                                      {item.colorRequest && (
                                        <div className="text-purple-600">
                                          Color: {item.colorRequest}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(getDisplayStatus(order))}`}>
                            {getStatusText(order)}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {/* Action buttons based on order status and balance */}
                          {order.status === 'pending' && (
                            <div className="space-x-2">
                              {/* Mark as Dispatched button */}
                              {order.type === 'retail' && (
                                <button
                                  onClick={() => handleDispatchRetailOrder(order)}
                                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                                  title="Dispatch retail order"
                                >
                                  Dispatch
                                </button>
                              )}

                              {/* Wholesale orders keep existing logic */}
                              {order.type === 'wholesale' && (
                                <button
                                  onClick={() => updateOrderStatus(order._id, 'dispatched')}
                                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs"
                                  title="Dispatch after advance payment"
                                >
                                  Dispatch
                                </button>
                              )}

                              {/* Cancel button always available for pending orders */}
                              <button
                                onClick={() => updateOrderStatus(order._id, 'cancelled')}
                                className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          )}

                          {/* For dispatched retail orders */}
                          {order.status === 'dispatched' && order.type === 'retail' && (
                            <div className="space-x-2">
                              {/* Balance Amount Received button */}
                              {order.balance > 0 && (
                                <button
                                  onClick={() => markBalanceReceived(order._id)}
                                  className="px-3 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-xs"
                                  title="Mark balance amount as received"
                                >
                                  Balance Received
                                </button>
                              )}

                              {/* Order Completed button */}
                              <button
                                onClick={() => updateOrderStatus(order._id, 'completed')}
                                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                                title="Mark order as completed"
                              >
                                Order Completed
                              </button>
                            </div>
                          )}

                          {/* For dispatched wholesale orders */}
                          {order.status === 'dispatched' && order.type === 'wholesale' && (
                            <button
                              onClick={() => handleCompleteWholesaleOrder(order)}
                              className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs"
                              title="Complete wholesale order"
                            >
                              Complete Order
                            </button>
                          )}

                          {/* Show current status for other states */}
                          {order.status !== 'pending' && order.status !== 'dispatched' && (
                            <span className="text-gray-500 text-xs">
                              {order.status === 'completed' ? '✅ Completed' :
                               order.status === 'cancelled' ? '❌ Cancelled' : '📦 Dispatched'}
                            </span>
                          )}

                          {/* Special case: dispatched wholesale with balance > 0 */}
                          {order.status === 'dispatched' && order.type === 'wholesale' && order.balance > 0 && (
                            <span className="text-orange-600 text-xs">
                              📦 Dispatched - Payment Pending
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    const current = pagination.page;
                    return page === 1 || page === pagination.totalPages ||
                           (page >= current - 1 && page <= current + 1);
                  })
                  .map((page, index, array) => (
                    <div key={page} className="flex items-center">
                      {index > 0 && array[index - 1] !== page - 1 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 rounded ${
                          pagination.page === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {page}
                      </button>
                    </div>
                  ))}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-2 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Balance Confirmation Modal for Wholesale Orders */}
      {showBalanceConfirmation && confirmOrderToComplete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Complete Wholesale Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{confirmOrderToComplete.orderNo}</strong><br/>
                Customer: <strong>{confirmOrderToComplete.customerName}</strong>
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-sm font-medium text-gray-800 mb-2">Is balance amount received?</p>
                {confirmOrderToComplete.balance > 0 ? (
                  <p className="text-sm text-red-600">
                    Outstanding Balance: ₹{confirmOrderToComplete.balance.toLocaleString('en-IN')}
                  </p>
                ) : (
                  <p className="text-sm text-green-600">
                    ✅ Balance is fully paid
                  </p>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => confirmCompleteWholesaleOrder(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  No, Cancel
                </button>
                <button
                  onClick={() => confirmCompleteWholesaleOrder(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  Yes, Complete Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Confirmation Modal for Retail Orders */}
      {showDispatchConfirmation && confirmOrderToDispatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                <svg className="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Dispatch Retail Order</h3>
              <p className="text-sm text-gray-500 mb-4">
                Order: <strong>{confirmOrderToDispatch.orderNo}</strong><br/>
                Customer: <strong>{confirmOrderToDispatch.customerName}</strong>
              </p>
              <div className="bg-orange-50 p-4 rounded-lg mb-4 border border-orange-200">
                <p className="text-sm font-medium text-orange-800 mb-2">⚠️ Dispatching will mark balance as received</p>
                {confirmOrderToDispatch.balance > 0 ? (
                  <div>
                    <p className="text-sm text-orange-700 mb-1">
                      Outstanding Balance: ₹{confirmOrderToDispatch.balance.toLocaleString('en-IN')}
                    </p>
                    <p className="text-sm text-orange-600">
                      This will be automatically marked as received upon dispatch.
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-green-600">
                    ✅ Balance is already fully paid
                  </p>
                )}
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => confirmDispatchRetailOrder(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => confirmDispatchRetailOrder(true)}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Confirm Dispatch
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrdersPage;
